import React, { useState, useEffect, useCallback } from 'react';
import { useAbdm } from '../context/AbdmContext';
import { handleVerifyOTP, handleViewProfile, handleRequestOTP } from '../../utils/api';
import { getFlowDescription } from '../../utils/helpers';
import { AuthConfig } from '../../utils/types';

export const OTPVerifier: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { otpInput, transactionId, authConfig, authData } = state;
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [showAbhaExistsModal, setShowAbhaExistsModal] = useState(false);
    const [existingAccounts, setExistingAccounts] = useState<string[]>([]);


    // Resend OTP timer state
    const [resendTimer, setResendTimer] = useState(60);
    const [resendCount, setResendCount] = useState(0);
    const MAX_RESENDS = 2;

    // Start countdown timer on mount and on resend
    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [resendTimer]);

    const onResendOTP = useCallback(async () => {
        if (resendCount >= MAX_RESENDS || !authData) return;
        setOtpError('');
        try {
            const otpResult = await handleRequestOTP(authData, authConfig);
            dispatch({ type: 'SET_TRANSACTION_ID', payload: otpResult.txnId });
            setResendTimer(60);
            setResendCount((prev) => prev + 1);
        } catch (error) {
            setOtpError((error as Error).message);
        }
    }, [authData, authConfig, resendCount, dispatch]);

    const getOtpTargetLabel = (): string => {
        if (authConfig.method === 'aadhaar' || authConfig.type === 'aadhaar-login') {
            return 'Aadhaar-linked mobile';
        }
        return 'Mobile';
    };

    const getOtpTargetValue = (): string => {
        if (authConfig.maskedMobile) {
            return authConfig.maskedMobile;
        }
        if (authConfig.method === 'aadhaar' && authConfig.type === 'enrollment') {
            return authConfig.aadhaarMobileInput;
        }
        if (authConfig.method === 'aadhaar' || authConfig.type === 'aadhaar-login') {
            return 'Aadhaar-linked mobile';
        }
        return authConfig.value;
    };

    const onVerifyOTP = async () => {
        setOtpLoading(true);
        setOtpError('');
        try {
            const result = await handleVerifyOTP(authData!, authConfig, transactionId, otpInput);

            console.log('OTP Verification Result:', {
                hasAccounts: result.accounts?.length,
                hasTToken: !!result.tToken,
                isEnrollment: result.isEnrollment,
                authConfigType: authConfig.type,
            });

            // Update txnId if ABDM returned a new one
            if (result.txnId) {
                dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            }

            // Check for existing ABHA during enrollment (profile with preferredAbhaAddress means account already exists)
            if (result.isEnrollment && result.profile) {
                const existingAddr = (result.profile as any).preferredAbhaAddress || result.profile.abhaAddress || '';
                const phrAddrs = (result.profile as any).phrAddress || [];
                if (existingAddr || phrAddrs.length > 0) {
                    const addrs = existingAddr ? [existingAddr, ...phrAddrs] : phrAddrs;
                    setExistingAccounts(addrs.filter(Boolean));
                    setShowAbhaExistsModal(true);
                    dispatch({ type: 'SET_USER_PROFILE', payload: result.profile });
                    dispatch({ type: 'SET_X_TOKEN', payload: result.xToken || '' });
                    return;
                }
            }

            // Check if we need to redirect to account selection
            const isCommunicationMobile = authConfig.type === 'communication-mobile';
            const hasAccounts = result.accounts && result.accounts.length > 0;

            if (hasAccounts && (result.accounts!.length > 1 || isCommunicationMobile)) {
                dispatch({ type: 'SET_AVAILABLE_ACCOUNTS', payload: result.accounts! });
                dispatch({ type: 'SET_OTP_INPUT', payload: otpInput });

                if (result.tToken) {
                    dispatch({ type: 'SET_T_TOKEN', payload: result.tToken });
                } else {
                    console.warn('No tToken received for multi-account flow');
                }

                dispatch({ type: 'SET_STEP', payload: 'userSelect' });
                return;
            }

            if (result.isEnrollment) {
                dispatch({ type: 'SET_X_TOKEN', payload: result.xToken || '' });
                dispatch({ type: 'SET_STEP', payload: 'abhaCreate' });
                return;
            }

            // Single account success
            if (result.profile) {
                const xToken = result.xToken || '';
                dispatch({ type: 'SET_X_TOKEN', payload: xToken });

                // Auto-fetch full profile for verification flows
                if (xToken && !result.isEnrollment && (
                    authConfig.type === 'abha-number' ||
                    authConfig.type === 'abha-address' ||
                    authConfig.type === 'aadhaar-login'
                )) {
                    try {
                        console.log('Auto-fetching full profile...');
                        const fullProfile = await handleViewProfile(authData!, xToken, authConfig);
                        dispatch({ type: 'SET_USER_PROFILE', payload: fullProfile });
                    } catch (profileError) {
                        console.warn('Failed to auto-fetch full profile, using partial profile:', profileError);
                        dispatch({ type: 'SET_USER_PROFILE', payload: result.profile });
                    }
                } else {
                    dispatch({ type: 'SET_USER_PROFILE', payload: result.profile });
                }

                dispatch({ type: 'SET_STEP', payload: 'success' });
            }
        } catch (error) {
            const errMsg = (error as Error).message || '';
            if (errMsg.startsWith('NO_ABHA_REGISTERED::')) {
                setOtpError('NO_ABHA_REGISTERED');
            } else if (errMsg.startsWith('NO_ABHA_ON_MOBILE::')) {
                setOtpError('NO_ABHA_ON_MOBILE');
            } else {
                setOtpError(errMsg);
            }
        } finally {
            setOtpLoading(false);
        }
    };

    const handleBack = () => {
        dispatch({ type: 'SET_OTP_INPUT', payload: '' });
        dispatch({ type: 'SET_STEP', payload: 'authType' });
    };

    const handleResetFlow = () => {
        dispatch({ type: 'RESET_FLOW' });
    };

    const handleGoToCreateAbha = () => {
        dispatch({ type: 'SET_AUTH_CONFIG', payload: {
            ...authConfig,
            type: 'enrollment',
            method: 'aadhaar',
            value: '',
            aadhaarMobileInput: ''
        } as AuthConfig });
        dispatch({ type: 'SET_ACTIVE_TAB', payload: 'creation' });
        dispatch({ type: 'SET_STEP', payload: 'authType' });
    };

    if (!authData) return null;

    return (
        <div className="step-container">
            <h2>Verify OTP</h2>
            <div className="flow-info">
                <p><strong>Flow:</strong> {getFlowDescription(authConfig.type, authConfig.method)}</p>
                <p><strong>OTP sent to:</strong> {getOtpTargetLabel()} - {getOtpTargetValue()}</p>
            </div>

            {/* Non-KYC warning */}
            {authConfig.nonKyc && (
                <div className="warning-banner" style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '0.9em' }}>
                    <strong>Non-KYC Account:</strong> This ABHA address is not KYC-verified. Aadhaar OTP is not available for non-KYC accounts.
                </div>
            )}

            <div className="form-group">
                <label htmlFor="otp">Enter 6-digit OTP</label>
                <input
                    id="otp"
                    type="text"
                    value={otpInput}
                    onChange={(e) => dispatch({ type: 'SET_OTP_INPUT', payload: e.target.value.replace(/\D/g, '') })}
                    placeholder="Enter OTP"
                    maxLength={6}
                    autoFocus
                />
            </div>

            {/* Resend OTP */}
            <div style={{ marginBottom: '12px', fontSize: '0.9em', color: '#666' }}>
                {resendTimer > 0 ? (
                    <span>Resend OTP in {resendTimer}s</span>
                ) : resendCount < MAX_RESENDS ? (
                    <button onClick={onResendOTP} className="btn-link" style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                        Resend OTP ({MAX_RESENDS - resendCount} remaining)
                    </button>
                ) : (
                    <span style={{ color: '#dc3545' }}>Maximum resend attempts reached</span>
                )}
            </div>

            <div className="action-buttons">
                <button onClick={handleBack} className="btn-secondary">
                    ← Back
                </button>
                <button onClick={onVerifyOTP} disabled={otpLoading || otpInput.length !== 6} className="btn-primary">
                    {otpLoading ? '🔍 Verifying...' : '✅ Verify OTP'}
                </button>
            </div>

            <div style={{ marginTop: '10px' }}>
                <button onClick={handleResetFlow} className="btn-outline" style={{ fontSize: '0.85em' }}>
                    Cancel & Start Over
                </button>
            </div>

            {/* Error messages */}
            {otpError && otpError === 'NO_ABHA_REGISTERED' && (
                <div className="error-message" style={{ marginTop: '12px' }}>
                    <p>No ABHA account is registered with this Aadhaar number.</p>
                    <button onClick={handleGoToCreateAbha} className="btn-link" style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0, textDecoration: 'underline', marginTop: '6px' }}>
                        Create a new ABHA instead
                    </button>
                </div>
            )}
            {otpError && otpError === 'NO_ABHA_ON_MOBILE' && (
                <div className="error-message" style={{ marginTop: '12px' }}>
                    <p>No ABHA account found linked to this mobile number.</p>
                    <button onClick={handleGoToCreateAbha} className="btn-link" style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0, textDecoration: 'underline', marginTop: '6px' }}>
                        Create a new ABHA instead
                    </button>
                </div>
            )}
            {otpError && otpError !== 'NO_ABHA_REGISTERED' && otpError !== 'NO_ABHA_ON_MOBILE' && (
                <div className="error-message">Error: {otpError}</div>
            )}

            {/* ABHA Already Exists Modal */}
            {showAbhaExistsModal && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0 }}>ABHA Account Exists</h3>
                        <p>An ABHA account already exists for this Aadhaar number:</p>
                        <ul style={{ paddingLeft: '20px' }}>
                            {existingAccounts.map((addr, i) => (
                                <li key={i} style={{ marginBottom: '4px' }}><strong>{addr}</strong></li>
                            ))}
                        </ul>
                        <p style={{ fontSize: '0.9em', color: '#666' }}>You can verify your existing ABHA instead of creating a new one.</p>
                        <div className="action-buttons" style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                            <button onClick={() => {
                                setShowAbhaExistsModal(false);
                                dispatch({ type: 'SET_ACTIVE_TAB', payload: 'verification' });
                                dispatch({ type: 'SET_STEP', payload: 'authType' });
                            }} className="btn-primary" style={{ flex: 1 }}>
                                Go to Verification
                            </button>
                            <button onClick={() => setShowAbhaExistsModal(false)} className="btn-secondary" style={{ flex: 1 }}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
